/**
 * Recovery Orchestrator
 * 
 * Centralized recovery system that manages different notification recovery scenarios
 * with clear boundaries and orchestrated handling.
 * 
 * Key Features:
 * - Scenario-based recovery (real-time, short-gap, session-gap, long-gap)
 * - Clear thresholds and boundaries between systems
 * - Coordinated recovery to prevent overlaps and conflicts
 * - Enhanced event processing based on gap size and importance
 */

import { apiClient } from './api';
import { unifiedActivityTracker } from './unifiedActivityTracker';
import { notificationService } from './notificationService';
import { enhancedToastService } from './enhancedToastService';
import { useNotificationStore } from '../store/notificationStore';

interface RecoveryScenario {
  type: 'real-time' | 'short-gap' | 'session-gap' | 'long-gap';
  shouldTrigger: (gapMs: number, eventsCount: number) => boolean;
  handler: (params: RecoveryParams) => Promise<RecoveryResult>;
  description: string;
}

interface RecoveryParams {
  since_event_id?: string;
  since_timestamp?: number;
  last_user_activity?: string;
  lookback_window_ms?: number;
  activity_gap_ms?: number;
  user_id?: string;
}

interface RecoveryResult {
  success: boolean;
  scenario: string;
  eventsProcessed: number;
  toastsShown: number;
  aggregated: boolean;
  error?: string;
  gap_hours?: number;
}

interface NotificationEvent {
  id: string;
  event_type: string;
  event_timestamp: number;
  data: any;
}

interface AggregatedEventSummary {
  aggregated: boolean;
  gap_hours: number;
  total_events: number;
  summary_messages: string[];
  strategy_summaries: any[];
  total_pnl: number;
  system_notification_id?: string;
  time_range: {
    start: string;
    end: string;
  };
}

export class RecoveryOrchestrator {
  private static instance: RecoveryOrchestrator;
  private scenarios: RecoveryScenario[];
  private isProcessing = false;
  private lastRecoveryTime = 0;
  private readonly MIN_RECOVERY_INTERVAL = 5000; // 5 seconds minimum between recoveries

  private constructor() {
    this.scenarios = [
      {
        type: 'real-time',
        shouldTrigger: (gap) => gap < 30 * 1000, // < 30 seconds
        handler: this.handleRealTime.bind(this),
        description: 'Real-time connection active, no recovery needed'
      },
      {
        type: 'short-gap',
        shouldTrigger: (gap) => gap >= 30 * 1000 && gap < 30 * 60 * 1000, // 30 seconds to 30 minutes
        handler: this.handleShortGap.bind(this),
        description: 'Short gap recovery with individual event toasts'
      },
      {
        type: 'session-gap',
        shouldTrigger: (gap) => gap >= 30 * 60 * 1000 && gap < 6 * 60 * 60 * 1000, // 30 minutes to 6 hours
        handler: this.handleSessionGap.bind(this),
        description: 'Session gap recovery with selective toasts'
      },
      {
        type: 'long-gap',
        shouldTrigger: (gap) => gap >= 6 * 60 * 60 * 1000, // >= 6 hours
        handler: this.handleLongGap.bind(this),
        description: 'Long gap recovery with aggregated summary'
      }
    ];
  }

  public static getInstance(): RecoveryOrchestrator {
    if (!RecoveryOrchestrator.instance) {
      RecoveryOrchestrator.instance = new RecoveryOrchestrator();
    }
    return RecoveryOrchestrator.instance;
  }

  /**
   * Main orchestration method - determines and executes appropriate recovery scenario
   */
  public async orchestrateRecovery(forceRefresh = false): Promise<RecoveryResult> {
    // Prevent concurrent recoveries
    if (this.isProcessing && !forceRefresh) {
      console.log('🚫 Recovery orchestrator: Already processing, skipping');
      return {
        success: false,
        scenario: 'skipped',
        eventsProcessed: 0,
        toastsShown: 0,
        aggregated: false,
        error: 'Already processing'
      };
    }

    // Throttle recovery attempts
    const now = Date.now();
    if (now - this.lastRecoveryTime < this.MIN_RECOVERY_INTERVAL && !forceRefresh) {
      console.log('🚫 Recovery orchestrator: Too soon since last recovery, skipping');
      return {
        success: false,
        scenario: 'throttled',
        eventsProcessed: 0,
        toastsShown: 0,
        aggregated: false,
        error: 'Recovery throttled'
      };
    }

    this.isProcessing = true;
    this.lastRecoveryTime = now;

    try {
      // Calculate session gap (not activity gap which gets updated on app open)
      const sessionGap = unifiedActivityTracker.getSessionGap();
      
      // Determine appropriate scenario
      const scenario = this.scenarios.find(s => s.shouldTrigger(sessionGap, 0));
      
      if (!scenario) {
        console.warn('⚠️ Recovery orchestrator: No matching scenario found');
        return {
          success: false,
          scenario: 'no-match',
          eventsProcessed: 0,
          toastsShown: 0,
          aggregated: false,
          error: 'No matching recovery scenario'
        };
      }

      console.log(`🎯 Recovery orchestrator: Using ${scenario.type} strategy for ${(sessionGap / 60000).toFixed(1)}min gap - ${scenario.description}`);

      // Get recovery parameters from activity tracker
      const params = unifiedActivityTracker.getRecoveryParams();

      // Execute scenario handler
      const result = await scenario.handler(params);
      
      console.log(`✅ Recovery orchestrator: ${scenario.type} completed - ${result.eventsProcessed} events, ${result.toastsShown} toasts`);
      
      return result;

    } catch (error) {
      console.error('❌ Recovery orchestrator failed:', error);
      return {
        success: false,
        scenario: 'error',
        eventsProcessed: 0,
        toastsShown: 0,
        aggregated: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Real-time scenario: Connection is active, no recovery needed
   */
  private async handleRealTime(_params: RecoveryParams): Promise<RecoveryResult> {
    console.log('⚡ Recovery orchestrator: Real-time mode - no recovery needed');
    
    return {
      success: true,
      scenario: 'real-time',
      eventsProcessed: 0,
      toastsShown: 0,
      aggregated: false
    };
  }

  /**
   * Short gap scenario: Individual events with toasts for recent important events
   */
  private async handleShortGap(params: RecoveryParams): Promise<RecoveryResult> {
    console.log('🔄 Recovery orchestrator: Short gap recovery - individual events with toasts');

    try {
      const response = await apiClient.get('/events/since', { 
        params,
        timeout: 15000
      });

      const result = response.data;
      let eventsProcessed = 0;
      let toastsShown = 0;

      if (result.aggregated && result.total_events > 0) {
        // Shouldn't happen for short gaps, but handle gracefully
        console.log('📊 Short gap returned aggregated data unexpectedly');
        await this.processAggregatedEvents(result);
        return {
          success: true,
          scenario: 'short-gap',
          eventsProcessed: result.total_events,
          toastsShown: 1, // Aggregated toast
          aggregated: true,
          gap_hours: result.gap_hours
        };
      } else if (result.events && result.events.length > 0) {
        console.log(`📋 Processing ${result.events.length} short-gap events`);
        
        for (const event of result.events) {
          await this.processNotificationEvent(event, true, params.activity_gap_ms || 0);
          eventsProcessed++;
          
          // Show toasts for important events in short gaps
          if (this.shouldShowShortGapToast(event, params.activity_gap_ms || 0)) {
            this.showRecoveryToast(event, true);
            toastsShown++;
          }
        }

        // Update last seen event
        const latestEvent = result.events[result.events.length - 1];
        unifiedActivityTracker.updateLastSeenEventId(latestEvent.id);
      }

      return {
        success: true,
        scenario: 'short-gap',
        eventsProcessed,
        toastsShown,
        aggregated: false,
        gap_hours: params.activity_gap_ms ? (params.activity_gap_ms / (60 * 60 * 1000)) : 0
      };

    } catch (error) {
      console.error('❌ Short gap recovery failed:', error);
      throw error;
    }
  }

  /**
   * Session gap scenario: Selective event processing with moderate toast filtering
   */
  private async handleSessionGap(params: RecoveryParams): Promise<RecoveryResult> {
    console.log('📅 Recovery orchestrator: Session gap recovery - selective event processing');

    try {
      const response = await apiClient.get('/events/since', { 
        params,
        timeout: 20000
      });

      const result = response.data;
      let eventsProcessed = 0;
      let toastsShown = 0;

      if (result.aggregated && result.total_events > 0) {
        await this.processAggregatedEvents(result);
        return {
          success: true,
          scenario: 'session-gap',
          eventsProcessed: result.total_events,
          toastsShown: 1, // Aggregated toast
          aggregated: true,
          gap_hours: result.gap_hours
        };
      } else if (result.events && result.events.length > 0) {
        console.log(`📋 Processing ${result.events.length} session-gap events`);
        
        for (const event of result.events) {
          await this.processNotificationEvent(event, true, params.activity_gap_ms || 0);
          eventsProcessed++;
          
          // Show toasts for important events only
          if (this.shouldShowSessionGapToast(event, params.activity_gap_ms || 0)) {
            this.showRecoveryToast(event, true);
            toastsShown++;
          }
        }

        // 🔥 NEW: Add fallback summary if multiple events but few/no toasts shown
        if (eventsProcessed >= 2 && toastsShown <= 1) {
          console.log(`📊 Session-gap: Showing summary for ${eventsProcessed} events (only ${toastsShown} individual toasts shown)`);
          this.showSessionGapSummary(result.events, params.activity_gap_ms || 0);
          toastsShown++; // Count the summary toast
        }

        // Update last seen event
        const latestEvent = result.events[result.events.length - 1];
        unifiedActivityTracker.updateLastSeenEventId(latestEvent.id);
      }

      return {
        success: true,
        scenario: 'session-gap',
        eventsProcessed,
        toastsShown,
        aggregated: false,
        gap_hours: params.activity_gap_ms ? (params.activity_gap_ms / (60 * 60 * 1000)) : 0
      };

    } catch (error) {
      console.error('❌ Session gap recovery failed:', error);
      throw error;
    }
  }

  /**
   * Long gap scenario: Aggregated summary with minimal individual processing
   */
  private async handleLongGap(params: RecoveryParams): Promise<RecoveryResult> {
    console.log('📊 Recovery orchestrator: Long gap recovery - aggregated summary');

    try {
      const response = await apiClient.get('/events/since', { 
        params,
        timeout: 25000
      });

      const result = response.data;

      if (result.aggregated && result.total_events > 0) {
        await this.processAggregatedEvents(result);
        return {
          success: true,
          scenario: 'long-gap',
          eventsProcessed: result.total_events,
          toastsShown: 1, // Aggregated toast
          aggregated: true,
          gap_hours: result.gap_hours
        };
      } else if (result.events && result.events.length > 0) {
        // Force aggregation for long gaps even if backend didn't aggregate
        console.log(`📊 Long gap returned individual events (${result.events.length}), creating summary`);
        
        // Process events silently (no toasts)
        for (const event of result.events) {
          await this.processNotificationEvent(event, true, params.activity_gap_ms || 0);
        }

        // Create summary notification
        this.createLongGapSummary(result.events, params.activity_gap_ms || 0);

        // Update last seen event
        const latestEvent = result.events[result.events.length - 1];
        unifiedActivityTracker.updateLastSeenEventId(latestEvent.id);

        return {
          success: true,
          scenario: 'long-gap',
          eventsProcessed: result.events.length,
          toastsShown: 1, // Summary toast
          aggregated: true,
          gap_hours: params.activity_gap_ms ? (params.activity_gap_ms / (60 * 60 * 1000)) : 0
        };
      }

      return {
        success: true,
        scenario: 'long-gap',
        eventsProcessed: 0,
        toastsShown: 0,
        aggregated: false,
        gap_hours: params.activity_gap_ms ? (params.activity_gap_ms / (60 * 60 * 1000)) : 0
      };

    } catch (error) {
      console.error('❌ Long gap recovery failed:', error);
      throw error;
    }
  }

  /**
   * Determine if toast should be shown for short gap events
   */
  private shouldShowShortGapToast(event: NotificationEvent, gapMs: number): boolean {
    const gapMinutes = gapMs / (60 * 1000);
    
    switch (event.event_type) {
      case 'trade_closed':
      case 'position_opened':
      case 'position_closed':
        return true; // 🔥 FIX: Always show critical events regardless of gap
      case 'position_milestone':
        const milestone = Math.abs(event.data.milestone_percentage || 0);
        return milestone >= 5; // Show significant milestones only
      default:
        return gapMinutes <= 15; // Show other events if gap < 15 minutes
    }
  }

  /**
   * Determine if toast should be shown for session gap events
   */
  private shouldShowSessionGapToast(event: NotificationEvent, _gapMs: number): boolean {
    switch (event.event_type) {
      case 'trade_closed':
      case 'position_opened':
      case 'position_closed':
        return true; // 🔥 FIX: Always show critical events regardless of gap
      case 'position_milestone':
        const milestone = Math.abs(event.data.milestone_percentage || 0);
        return milestone >= 10; // Show significant milestones only
      default:
        return false; // Skip other events for session gaps
    }
  }

  /**
   * Process individual notification event (reused from useUnifiedNotifications)
   */
  private async processNotificationEvent(event: NotificationEvent, isRecovery: boolean, _gapMs: number): Promise<void> {
    try {
      console.log(`📝 Recovery orchestrator: Processing ${event.event_type} event (${isRecovery ? 'recovery' : 'live'})`);
      
      // Create notification based on event type
      let notification = null;
      
      switch (event.event_type) {
        case 'trade_closed':
          notification = notificationService.createTradeClosedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            strategy: event.data.strategy,
            pnl: event.data.pnl,
            roi: event.data.roi_percentage,
            tradeId: event.data.trade_id,
            exectype: event.data.exectype,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent: isRecovery
          });
          break;
          
        case 'position_opened':
          notification = notificationService.createPositionOpenedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            strategy: event.data.strategy,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent: isRecovery
          });
          break;
          
        case 'position_closed':
          notification = notificationService.createPositionClosedNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            finalPnl: event.data.final_pnl,
            strategy: event.data.strategy,
            tradeId: event.data.trade_id,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent: isRecovery
          });
          break;
          
        case 'position_milestone':
          const storedMilestonePercentage = parseFloat(event.data.milestone_percentage) || 0;
          const trendDirection = storedMilestonePercentage < 0 ? 'declining' : 'improving';
          
          notification = notificationService.createMilestoneNotification({
            symbol: event.data.symbol,
            account: event.data.account,
            side: event.data.side,
            milestone_percentage: storedMilestonePercentage,
            currentPnl: event.data.current_pnl || event.data.unrealised_pnl,
            milestone_trend: trendDirection,
            strategy: event.data.strategy,
            tradeId: event.data.trade_id,
            eventTimestamp: event.event_timestamp,
            isRecoveryEvent: isRecovery
          });
          break;
          
        default:
          // 🔥 FIX: Don't create local notifications for unknown event types
          // Unknown events should be handled by backend or ignored gracefully
          console.log(`⚠️ Unhandled event type: ${event.event_type} - backend should handle all event types`);
          
          // No notification created - backend should handle unknown event types
          notification = null;
          break;
      }
      
      if (notification) {
        const { addNotification } = useNotificationStore.getState();
        addNotification(notification);
      }
      
    } catch (error) {
      console.error('❌ Recovery orchestrator: Failed to process notification event:', error);
    }
  }

  /**
   * Process aggregated events summary
   */
  private async processAggregatedEvents(result: AggregatedEventSummary): Promise<void> {
    const { gap_hours, total_events, strategy_summaries, system_notification_id } = result;
    
    console.log(`📊 Recovery orchestrator: Processing aggregated events: ${total_events} events over ${gap_hours.toFixed(1)} hours`);
    
    if (total_events === 0) {
      console.log('📊 Skipping aggregated notification - no events to report');
      return;
    }

    // 🔥 NEW: Check for duplicate aggregated summaries
    const aggregatedHash = this.createAggregatedHash(result);
    if (this.hasProcessedAggregatedSummary(aggregatedHash)) {
      console.log('🔄 Skipping duplicate aggregated summary - already processed this batch');
      return;
    }
    
    // 🔥 FIX: Backend now creates the system notification - no need to create locally
    // The system notification is already stored in the database and will be fetched
    // via fetchStoredNotifications on the next app startup
    
    if (system_notification_id) {
      console.log(`✅ Backend created trading activity summary notification: ${system_notification_id}`);
    } else {
      console.log('⚠️ Backend did not create system notification - may need to check backend logs');
    }
    
    // Show aggregated toast for immediate user feedback
    this.showAggregatedToast(result);
    
    // 🔥 CRITICAL FIX: Update lastSeenEventId to prevent reprocessing same events
    if (result.time_range?.end) {
      // Derive latest event ID from the end timestamp
      const latestEventId = this.deriveLatestEventIdFromTimestamp(result.time_range.end);
      unifiedActivityTracker.updateLastSeenEventId(latestEventId);
      console.log(`📌 Updated lastSeenEventId from aggregated response: ${latestEventId}`);
    } else {
      console.warn('⚠️ Aggregated response missing time_range.end - cannot update lastSeenEventId');
    }
    
    // Store breakdown for detailed view
    localStorage.setItem('lastAggregatedBreakdown', JSON.stringify(strategy_summaries));
  }

  /**
   * 🔥 NEW: Derive latest event ID from aggregated response timestamp
   * Creates a consistent event ID that backend can use for subsequent since queries
   */
  private deriveLatestEventIdFromTimestamp(endTimestamp: string): string {
    // Convert ISO timestamp to event ID format that backend expects
    // Format: aggregated_summary_<timestamp_ms>_<hash>
    try {
      const timestampMs = new Date(endTimestamp).getTime();
      const hash = this.generateEventHash(endTimestamp);
      
      // Use a consistent format that backend can recognize as "processed up to this point"
      return `aggregated_summary_${timestampMs}_${hash}`;
    } catch (error) {
      console.error('❌ Failed to derive event ID from timestamp:', error);
      // Fallback to timestamp-based ID
      return `aggregated_${Date.now()}`;
    }
  }

  /**
   * Generate consistent hash for event ID
   */
  private generateEventHash(input: string): string {
    // Simple hash generation for consistent event IDs
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
      const char = input.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36).substr(0, 8);
  }

  /**
   * 🔥 NEW: Format time context for aggregated events using event span instead of API gap
   */
  private formatAggregatedTimeContext(result: AggregatedEventSummary): string {
    // For very small gaps (< 5 minutes), use "Recent activity" instead of misleading time spans
    if (result.gap_hours < (5 / 60)) {
      return 'Recent activity';
    }
    
    // If we have time range, use the event span (more meaningful)
    if (result.time_range?.start && result.time_range?.end) {
      const startTime = new Date(result.time_range.start).getTime();
      const endTime = new Date(result.time_range.end).getTime();
      const spanHours = (endTime - startTime) / (60 * 60 * 1000);
      
      // Use event span for context
      const spanText = this.formatTimeGap(spanHours);
      return `While away (${spanText})`;
    }
    
    // Fallback to gap hours but with better messaging
    if (result.gap_hours >= 1) {
      return `While away (${this.formatTimeGap(result.gap_hours)})`;
    } else {
      return 'Recent updates';
    }
  }

  /**
   * 🔥 NEW: Create hash for aggregated summary deduplication
   */
  private createAggregatedHash(result: AggregatedEventSummary): string {
    // Create hash based on content that should identify unique aggregated summaries
    const hashInput = JSON.stringify({
      total_events: result.total_events,
      total_pnl: result.total_pnl,
      time_range: result.time_range,
      strategy_summaries: result.strategy_summaries?.map(s => ({
        strategy: s.strategy,
        trades: s.trades,
        pnl: s.pnl
      }))
    });
    
    return this.generateEventHash(hashInput);
  }

  /**
   * 🔥 NEW: Check if aggregated summary has been processed
   */
  private hasProcessedAggregatedSummary(hash: string): boolean {
    const processedHashes = this.getProcessedAggregatedHashes();
    const isProcessed = processedHashes.includes(hash);
    
    if (!isProcessed) {
      // Store this hash as processed
      this.storeProcessedAggregatedHash(hash);
    }
    
    return isProcessed;
  }

  /**
   * Get list of processed aggregated summary hashes
   */
  private getProcessedAggregatedHashes(): string[] {
    try {
      const stored = localStorage.getItem('processedAggregatedHashes');
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn('Failed to parse processed aggregated hashes:', error);
      return [];
    }
  }

  /**
   * Store aggregated summary hash as processed
   */
  private storeProcessedAggregatedHash(hash: string): void {
    try {
      const existing = this.getProcessedAggregatedHashes();
      const updated = [...existing, hash];
      
      // Keep only last 50 hashes to prevent localStorage bloat
      const trimmed = updated.slice(-50);
      
      localStorage.setItem('processedAggregatedHashes', JSON.stringify(trimmed));
    } catch (error) {
      console.warn('Failed to store processed aggregated hash:', error);
    }
  }

  /**
   * Create summary for long gaps that weren't aggregated by backend
   * NOTE: This is a fallback - backend should normally handle aggregation
   */
  private createLongGapSummary(events: NotificationEvent[], gapMs: number): void {
    const gapHours = gapMs / (60 * 60 * 1000);
    
    // Filter out internal/system events that shouldn't be shown to users
    const USER_FACING_EVENT_TYPES = new Set([
      'trade_closed',
      'position_opened', 
      'position_closed',
      'position_modified',
      'position_milestone'
    ]);

    const userFacingEvents = events.filter(event => 
      USER_FACING_EVENT_TYPES.has(event.event_type)
    );

    const eventCounts = userFacingEvents.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const summaryMessages = Object.entries(eventCounts)
      .map(([type, count]) => `${count} ${type.replace('_', ' ')} event${count > 1 ? 's' : ''}`)
      .slice(0, 3); // Limit to top 3 event types

    console.log(`⚠️ [Recovery] Backend didn't aggregate ${userFacingEvents.length} user-facing events (${events.length} total) - this is unexpected`);
    console.log(`⚠️ [Recovery] Creating fallback summary for ${gapHours.toFixed(1)}h gap: ${summaryMessages.join(', ')}`);

    // 🔥 FIX: Don't create local notification - this should be handled by backend
    // Just show toast for immediate feedback, but rely on backend to create persisted notification
    
    // Show summary toast for user feedback
    enhancedToastService.showToast(
      `📊 While away (${this.formatTimeGap(gapHours)}): ${summaryMessages.join(', ')}`,
      {
        duration: 8000,
        icon: '📊',
        style: {
          background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
          color: '#fff',
          borderRadius: '12px',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          backdropFilter: 'blur(12px)',
          padding: '16px',
          fontSize: '14px',
          fontWeight: '500',
          maxWidth: '400px'
        }
      },
      undefined,
      true
    );
  }

  /**
   * 🔥 NEW: Show session gap summary for moderate gaps with mixed events
   */
  private showSessionGapSummary(events: NotificationEvent[], gapMs: number): void {
    const gapHours = gapMs / (60 * 60 * 1000);
    
    // Filter out internal/system events that shouldn't be shown to users
    const USER_FACING_EVENT_TYPES = new Set([
      'trade_closed',
      'position_opened', 
      'position_closed',
      'position_modified',
      'position_milestone'
    ]);

    const userFacingEvents = events.filter(event => 
      USER_FACING_EVENT_TYPES.has(event.event_type)
    );

    const eventCounts = userFacingEvents.reduce((acc, event) => {
      acc[event.event_type] = (acc[event.event_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Create more concise summary for session gaps (shorter than long gaps)
    const topEvents = Object.entries(eventCounts)
      .sort(([,a], [,b]) => b - a) // Sort by count descending
      .slice(0, 2) // Limit to top 2 event types for session gaps
      .map(([type, count]) => {
        const displayType = type.replace('_', ' ').replace('position ', '');
        return count > 1 ? `${count} ${displayType}s` : `${count} ${displayType}`;
      });

    const summaryText = topEvents.length > 0 
      ? topEvents.join(' & ') 
      : `${events.length} events`;

    // Create notification
    const summaryNotification = notificationService.createNotification({
      type: 'system',
      title: 'Session Activity Summary',
      message: `While away (${this.formatTimeGap(gapHours)}): ${summaryText}`,
      metadata: {
        isRecoveryEvent: true,
        eventTimestamp: Date.now(),
        sessionGapDuration: gapMs,
        sessionGapSummary: true,
        eventCounts
      }
    });
    
    const { addNotification } = useNotificationStore.getState();
    addNotification(summaryNotification);

    // Show summary toast with lighter styling than long-gap
    enhancedToastService.showToast(
      `📋 While away (${this.formatTimeGap(gapHours)}): ${summaryText}`,
      {
        duration: 6000,
        icon: '📋',
        style: {
          background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', // Blue gradient
          color: '#fff',
          borderRadius: '10px',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          backdropFilter: 'blur(8px)',
          padding: '14px',
          fontSize: '13px',
          fontWeight: '500',
          maxWidth: '350px'
        }
      },
      undefined,
      true
    );
  }

  /**
   * Show recovery toast for individual events
   */
  private showRecoveryToast(event: NotificationEvent, isRecovery: boolean): void {
    const prefix = isRecovery ? '⏰ While away: ' : '';
    
    if (event.event_type === 'trade_closed') {
      const pnl = event.data.pnl;
      const symbol = event.data.symbol;
      const pnlText = pnl > 0 ? `+$${pnl.toFixed(2)}` : `$${pnl.toFixed(2)}`;
      const emoji = pnl > 0 ? '📈' : '📉';
      
      const message = `${prefix}${emoji} ${symbol} trade closed (${pnlText})`;
      
      enhancedToastService.showToast(message, {
        duration: isRecovery ? 6000 : 4000,
        style: {
          background: isRecovery 
            ? 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' // Purple for recovery
            : (pnl > 0 ? '#22c55e' : '#ef4444'),
          color: '#fff',
          border: isRecovery ? '1px solid rgba(139, 92, 246, 0.3)' : 'none'
        }
      }, undefined, isRecovery);
      
    } else if (event.event_type === 'position_opened') {
      const symbol = event.data.symbol;
      const message = `${prefix}🚀 New ${symbol} position opened`;
      
      enhancedToastService.showToast(message, {
        duration: isRecovery ? 6000 : 3000,
        style: {
          background: isRecovery 
            ? 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' // Purple for recovery
            : 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)', // Blue for real-time
          color: '#fff',
          border: isRecovery ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid rgba(59, 130, 246, 0.3)'
        }
      }, undefined, isRecovery);
    }
  }

  /**
   * Show aggregated toast for long offline periods
   */
  private showAggregatedToast(result: AggregatedEventSummary): void {
    const { total_events, summary_messages } = result;
    
    // 🔥 FIX: Use consistent time context logic
    const timeContext = this.formatAggregatedTimeContext(result);
    const summaryText = summary_messages?.length > 0 
      ? summary_messages.join(', ')
      : `${total_events} trading events`;
    
    const message = `📊 ${timeContext}: ${summaryText}`;
    
    enhancedToastService.showToast(message, {
      duration: 8000,
      icon: '📊',
      style: {
        background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
        color: '#fff',
        borderRadius: '12px',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        backdropFilter: 'blur(12px)',
        padding: '16px',
        fontSize: '14px',
        fontWeight: '500',
        maxWidth: '400px'
      }
    }, undefined, true);
  }

  /**
   * Format time gap for display
   */
  private formatTimeGap(hours: number): string {
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    } else if (hours >= 1) {
      return `${Math.floor(hours)} hour${Math.floor(hours) !== 1 ? 's' : ''}`;
    } else {
      const minutes = Math.floor(hours * 60);
      if (minutes <= 0) {
        return 'moments ago';
      }
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  }

  /**
   * Get current orchestrator status
   */
  public getStatus(): { isProcessing: boolean; lastRecoveryTime: number } {
    return {
      isProcessing: this.isProcessing,
      lastRecoveryTime: this.lastRecoveryTime
    };
  }

  /**
   * Reset orchestrator state (useful for testing)
   */
  public reset(): void {
    this.isProcessing = false;
    this.lastRecoveryTime = 0;
  }
}

// Export singleton instance
export const recoveryOrchestrator = RecoveryOrchestrator.getInstance();

// Export factory function for dependency injection
export const createRecoveryOrchestrator = (): RecoveryOrchestrator => {
  return RecoveryOrchestrator.getInstance();
};